'use client';

import { Box, Slider, Stack, Typography } from '@mui/material';
import { FilterSectionHeading } from '@odys/design-system/components/FilterSectionHeading';

const PAX_MIN = 1;
const PAX_MAX = 8;

const PAX_MARKS = Array.from({ length: PAX_MAX - PAX_MIN + 1 }, (_, i) => {
    const value = PAX_MIN + i;
    return { value, label: value === PAX_MAX ? `${PAX_MAX}+` : String(value) };
});

const formatPax = (value: number) => (value >= PAX_MAX ? `${PAX_MAX}+` : String(value));

type PaxRange = [number, number];

type PaxRangeFilterProps = {
    label: string;
    value: PaxRange | null;
    onChange: (value: PaxRange | null) => void;
    resetLabel: string;
};

/**
 * Curseur "Nombre de pax" (Transports) : range-slider à deux curseurs
 * (taille de groupe `[min, max]`, 1 → 8 avec "8+" au max). Le contrat de requête
 * envoie les deux bornes ; le back garde le produit dont la capacité chevauche
 * l'intervalle demandé (cf. `pax-range`). "Réinitialiser" (visible si un range
 * est actif) efface le filtre.
 */
export function PaxRangeFilter({ label, value, onChange, resetLabel }: PaxRangeFilterProps) {
    return (
        <Stack spacing={1} data-testid="dmc-products-pax-filter">
            <Stack
                direction="row"
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <FilterSectionHeading>{label}</FilterSectionHeading>
                {value !== null && (
                    <Typography
                        variant="caption"
                        color="primary"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => onChange(null)}
                        data-testid="dmc-products-pax-filter-reset"
                    >
                        {resetLabel}
                    </Typography>
                )}
            </Stack>
            {/* Padding horizontal pour contenir les pouces et les libellés de
                graduations extrêmes ("1" et "8+") dans la colonne de filtres
                (260px) — sinon le slider déborde et crée un scroll horizontal. */}
            <Box sx={{ px: 2.25 }}>
                <Slider
                    value={value ?? [PAX_MIN, PAX_MAX]}
                    min={PAX_MIN}
                    max={PAX_MAX}
                    step={1}
                    marks={PAX_MARKS}
                    valueLabelDisplay="auto"
                    valueLabelFormat={formatPax}
                    onChange={(_, next) => onChange(next as PaxRange)}
                    aria-label={label}
                />
            </Box>
        </Stack>
    );
}
